import { useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';

const { t } = i18n;

interface OrderLineRef {
  id: string;
  qty: number;
  label: string;
}

interface DraftGroupLine {
  orderLineId: string;
  qty: number;
}

interface ExistingGroup {
  address: string;
  city?: string;
  street?: string;
  houseNumber?: string;
  unitNumber?: string;
  addressComment: string;
  phone: string;
  phones?: string[];
  preferredDate: string;
  pickupAtWarehouse: boolean;
  deliveryFeeCents: number;
  sortOrder: number;
  lines: DraftGroupLine[];
}

interface DraftGroup extends Omit<
  ExistingGroup,
  'city' | 'street' | 'houseNumber' | 'unitNumber' | 'phones'
> {
  city: string;
  street: string;
  houseNumber: string;
  unitNumber: string;
  phones: string[];
}

function legacyAddressParts(address: string) {
  const parts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const city = parts.length > 1 ? parts.at(-1)! : '';
  const streetLine = parts.length > 1 ? parts.slice(0, -1).join(', ') : (parts[0] ?? '');
  const match = /^(.*\D)\s+(\d+[\p{L}\d/-]*)$/u.exec(streetLine);
  return {
    city,
    street: match?.[1]?.trim() ?? streetLine,
    houseNumber: match?.[2] ?? '',
    unitNumber: '',
  };
}

function hydrateGroup(group: ExistingGroup): DraftGroup {
  const hasStructuredAddress = Boolean(
    group.city || group.street || group.houseNumber || group.unitNumber,
  );
  const address = hasStructuredAddress
    ? {
        city: group.city ?? '',
        street: group.street ?? '',
        houseNumber: group.houseNumber ?? '',
        unitNumber: group.unitNumber ?? '',
      }
    : legacyAddressParts(group.address);
  const phones = (group.phones?.length ? group.phones : [group.phone]).filter(Boolean);
  return { ...group, ...address, phones: phones.length > 0 ? phones : [''] };
}

function composeAddress(group: DraftGroup): string {
  const streetLine = [group.street.trim(), group.houseNumber.trim()].filter(Boolean).join(' ');
  return [streetLine, group.unitNumber.trim(), group.city.trim()].filter(Boolean).join(', ');
}

function fullOrderLineAllocation(orderLines: OrderLineRef[]): DraftGroupLine[] {
  return orderLines.map((line) => ({ orderLineId: line.id, qty: line.qty }));
}

function normalizeSingleGroup(groups: DraftGroup[], orderLines: OrderLineRef[]): DraftGroup[] {
  if (groups.length !== 1) return groups;
  return [{ ...groups[0]!, lines: fullOrderLineAllocation(orderLines) }];
}

export function DeliveryGroupsEditor({
  orderId,
  orderLines,
  existing,
  onClose,
  onSaved,
}: {
  orderId: string;
  orderLines: OrderLineRef[];
  existing: ExistingGroup[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [groups, setGroups] = useState<DraftGroup[]>(() =>
    normalizeSingleGroup(
      existing.length > 0
        ? existing.map(hydrateGroup)
        : [
            {
              address: '',
              city: '',
              street: '',
              houseNumber: '',
              unitNumber: '',
              addressComment: '',
              phone: '',
              phones: [''],
              preferredDate: '',
              pickupAtWarehouse: false,
              deliveryFeeCents: 0,
              sortOrder: 0,
              lines: [],
            },
          ],
      orderLines,
    ),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addGroup() {
    setGroups((prev) => [
      ...prev,
      {
        address: '',
        city: '',
        street: '',
        houseNumber: '',
        unitNumber: '',
        addressComment: '',
        phone: '',
        phones: [''],
        preferredDate: '',
        pickupAtWarehouse: false,
        deliveryFeeCents: 0,
        sortOrder: prev.length,
        lines: [],
      },
    ]);
  }

  function setGroupField<K extends keyof DraftGroup>(i: number, key: K, value: DraftGroup[K]) {
    setGroups((prev) => prev.map((g, j) => (j === i ? { ...g, [key]: value } : g)));
  }

  function setLineQty(gi: number, orderLineId: string, qty: number) {
    setGroups((prev) =>
      prev.map((g, j) => {
        if (j !== gi) return g;
        const found = g.lines.find((l) => l.orderLineId === orderLineId);
        if (qty <= 0) {
          return { ...g, lines: g.lines.filter((l) => l.orderLineId !== orderLineId) };
        }
        if (found) {
          return {
            ...g,
            lines: g.lines.map((l) => (l.orderLineId === orderLineId ? { ...l, qty } : l)),
          };
        }
        return { ...g, lines: [...g.lines, { orderLineId, qty }] };
      }),
    );
  }

  function setPhone(groupIndex: number, phoneIndex: number, value: string) {
    setGroups((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              phones: group.phones.map((phone, currentPhoneIndex) =>
                currentPhoneIndex === phoneIndex ? value : phone,
              ),
            }
          : group,
      ),
    );
  }

  function addPhone(groupIndex: number) {
    setGroups((current) =>
      current.map((group, index) =>
        index === groupIndex ? { ...group, phones: [...group.phones, ''] } : group,
      ),
    );
  }

  function removePhone(groupIndex: number, phoneIndex: number) {
    setGroups((current) =>
      current.map((group, index) => {
        if (index !== groupIndex) return group;
        const phones = group.phones.filter((_, indexToRemove) => indexToRemove !== phoneIndex);
        return { ...group, phones: phones.length > 0 ? phones : [''] };
      }),
    );
  }

  function removeGroup(index: number) {
    setGroups((prev) =>
      normalizeSingleGroup(
        prev.filter((_, groupIndex) => groupIndex !== index),
        orderLines,
      ),
    );
  }

  function totalAcrossGroups(orderLineId: string): number {
    return groups.reduce(
      (sum, g) => sum + (g.lines.find((l) => l.orderLineId === orderLineId)?.qty ?? 0),
      0,
    );
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const groupsToSave = normalizeSingleGroup(groups, orderLines);
      await api(`/orders/${orderId}/delivery-groups`, {
        method: 'PUT',
        body: {
          groups: groupsToSave.map((g, i) => ({
            address: composeAddress(g),
            city: g.city || undefined,
            street: g.street || undefined,
            houseNumber: g.houseNumber || undefined,
            unitNumber: g.unitNumber || undefined,
            addressComment: g.addressComment || undefined,
            phones: g.phones.map((phone) => phone.trim()).filter(Boolean),
            preferredDate: g.preferredDate || undefined,
            pickupAtWarehouse: g.pickupAtWarehouse,
            deliveryFeeCents: g.deliveryFeeCents,
            sortOrder: i,
            lines: g.lines.filter((l) => l.qty > 0),
          })),
        },
      });
      onSaved();
    } catch {
      setError(t('order.groups.save_error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('order.groups.edit')} onClose={onClose}>
      <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1">
        {groups.map((g, gi) => (
          <div key={gi} className="rounded border border-border p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-2">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={g.pickupAtWarehouse}
                    onChange={(e) => setGroupField(gi, 'pickupAtWarehouse', e.target.checked)}
                  />
                  {t('order.groups.pickup_at_warehouse')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">{t('order.groups.city')}</span>
                    <input
                      value={g.city}
                      onChange={(e) => setGroupField(gi, 'city', e.target.value)}
                      className="w-full rounded border border-border bg-background px-2 py-1"
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">{t('order.groups.street')}</span>
                    <input
                      value={g.street}
                      onChange={(e) => setGroupField(gi, 'street', e.target.value)}
                      className="w-full rounded border border-border bg-background px-2 py-1"
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">{t('order.groups.house_number')}</span>
                    <input
                      value={g.houseNumber}
                      onChange={(e) => setGroupField(gi, 'houseNumber', e.target.value)}
                      className="w-full rounded border border-border bg-background px-2 py-1"
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">{t('order.groups.unit_number')}</span>
                    <input
                      value={g.unitNumber}
                      onChange={(e) => setGroupField(gi, 'unitNumber', e.target.value)}
                      className="w-full rounded border border-border bg-background px-2 py-1"
                    />
                  </label>
                </div>
                <label className="block space-y-1">
                  <span className="text-xs font-medium">{t('order.groups.address_comment')}</span>
                  <textarea
                    placeholder={t('order.groups.address_comment_hint')}
                    value={g.addressComment}
                    onChange={(e) => setGroupField(gi, 'addressComment', e.target.value)}
                    rows={2}
                    className="w-full rounded border border-border bg-background px-2 py-1"
                  />
                </label>
                <div className="space-y-2">
                  <span className="text-xs font-medium">{t('order.groups.phones')}</span>
                  {g.phones.map((phone, phoneIndex) => (
                    <div key={phoneIndex} className="flex gap-2">
                      <input
                        type="tel"
                        aria-label={`${t('order.groups.phone')} ${phoneIndex + 1}`}
                        placeholder={t('order.groups.phone')}
                        value={phone}
                        onChange={(e) => setPhone(gi, phoneIndex, e.target.value)}
                        className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1"
                      />
                      {g.phones.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => removePhone(gi, phoneIndex)}
                          aria-label={t('order.groups.remove_phone')}
                          className="rounded border border-border px-2 py-1"
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addPhone(gi)}
                    className="rounded border border-border px-2 py-1 text-xs"
                  >
                    {t('order.groups.add_phone')}
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeGroup(gi)}
                className="rounded border border-border px-2 py-1 text-xs"
              >
                ×
              </button>
            </div>
            {groups.length > 1 ? (
              <div className="border-t border-border pt-2 space-y-1">
                <div className="pb-1 text-xs font-medium">{t('order.groups.line_allocation')}</div>
                {orderLines.map((line) => {
                  const groupQty = g.lines.find((l) => l.orderLineId === line.id)?.qty ?? 0;
                  const used = totalAcrossGroups(line.id);
                  const overlap = used - line.qty;
                  return (
                    <div key={line.id} className="flex items-center gap-2 text-xs">
                      <span className="flex-1 text-muted-foreground">{line.label}</span>
                      <NumberInput
                        integer
                        min={0}
                        max={line.qty}
                        value={groupQty}
                        onChange={(v) => setLineQty(gi, line.id, v ?? 0)}
                        className="w-20 rounded border border-border bg-background px-2 py-1 tabular-nums"
                      />
                      <span
                        className={`tabular-nums w-16 text-right ${
                          overlap > 1e-6 ? 'text-red-600' : 'text-muted-foreground'
                        }`}
                      >
                        {used}/{line.qty}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        ))}
        <button
          type="button"
          onClick={addGroup}
          className="rounded border border-border px-3 py-1.5 text-sm"
        >
          {t('order.groups.add')}
        </button>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
